/**
 * Database Explorer — structured row CRUD contracts (Table Editor).
 *
 * The client sends INTENT (table + values + identity), never SQL. The route
 * handler builds a safe statement server-side from the introspected
 * columns/PK and runs it through the single gated runner. This is why
 * rows.* exists separately from sql.run.
 */

import { z } from "zod";
import type { Operation } from "../../types";
import { runResultSchema } from "./sql";

/** Per-column metadata the grid needs to render + type cell editors. */
export const columnMetaSchema = z.object({
  name: z.string(),
  type: z.string(),
  notnull: z.boolean(),
  pk: z.number(),
  fkTable: z.string().nullable(),
  fkColumn: z.string().nullable(),
});
export type ColumnMetaDto = z.infer<typeof columnMetaSchema>;

export const rowsListResponse = z.object({
  table: z.string(),
  columns: z.array(columnMetaSchema),
  rows: z.array(z.array(z.unknown())),
  total: z.number(),
  page: z.number(),
  page_size: z.number(),
  /** Columns forming the row identity for update/delete (PK, else ["rowid"]). */
  identity: z.array(z.string()),
  /** True when the grid included a synthetic `rowid` column for identity. */
  usesRowid: z.boolean(),
  /** False when the table has neither a PK nor a usable rowid (read-only). */
  editable: z.boolean(),
});
export type RowsListResponse = z.infer<typeof rowsListResponse>;

export const rowsListQuery = z.object({
  table: z.string().min(1),
  page: z.coerce.number().int().min(1).optional(),
  page_size: z.coerce.number().int().min(1).max(500).optional(),
  sort: z.string().optional(),
  dir: z.enum(["asc", "desc"]).optional(),
  q: z.string().optional(),
  /** JSON-encoded `{ column: value }` per-column equality filters. */
  filters: z.string().optional(),
});
export type RowsListQuery = z.infer<typeof rowsListQuery>;

export const rowsList: Operation<
  void,
  z.infer<typeof rowsListQuery>,
  void,
  z.infer<typeof rowsListResponse>
> = {
  method: "GET",
  path: () => "/api/database/rows",
  query: rowsListQuery,
  response: rowsListResponse,
};

const cellValue = z.union([z.string(), z.number(), z.boolean(), z.null()]);

export const rowsInsertBody = z.object({
  table: z.string().min(1),
  values: z.record(cellValue),
});
export const rowsInsert: Operation<
  void,
  void,
  z.infer<typeof rowsInsertBody>,
  z.infer<typeof runResultSchema>
> = {
  method: "POST",
  path: () => "/api/database/rows",
  body: rowsInsertBody,
  response: runResultSchema,
};

export const rowsUpdateBody = z.object({
  table: z.string().min(1),
  identity: z.record(cellValue),
  set: z.record(cellValue),
});
export const rowsUpdate: Operation<
  void,
  void,
  z.infer<typeof rowsUpdateBody>,
  z.infer<typeof runResultSchema>
> = {
  method: "PATCH",
  path: () => "/api/database/rows",
  body: rowsUpdateBody,
  response: runResultSchema,
};

export const rowsDeleteBody = z.object({
  table: z.string().min(1),
  identity: z.record(cellValue),
});
export const rowsDelete: Operation<
  void,
  void,
  z.infer<typeof rowsDeleteBody>,
  z.infer<typeof runResultSchema>
> = {
  method: "DELETE",
  path: () => "/api/database/rows",
  body: rowsDeleteBody,
  response: runResultSchema,
};

export const rows = {
  list: rowsList,
  insert: rowsInsert,
  update: rowsUpdate,
  delete: rowsDelete,
};
